import { createTheme, responsiveFontSizes } from '@mui/material/styles';
import vars from './vars.module.scss';

const recOneTheme = {
  palette: {
    primary: {
      main: vars.primary, // #7F00FF
      light: vars.primaryLight // #fce9e9
    },
    secondary: {
      main: vars.secondary
    },
    success: {
      main: vars.success
    },
    error: {
      main: vars.danger // #EF4444
    },
    background: {
      default: vars.primaryBackground, // #F0F0F0
      paper: vars.secondaryBackground // #FFFFFF
    },
    text: {
      primary: vars.gray1, // #0F172A
      secondary: vars.primaryGray // #64748B
    },
    divider: vars.lightGray // #E2E8F0
  },
  shape: {
    borderRadius: 6
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        'html, body': {
          background: vars.primaryBackground
        },
        a: {
          color: vars.primary
        },
        '.AuthContainer': {
          minHeight: '90vh',
          alignContent: 'center',
          justifyContent: 'center',
          margin: '25px 0'
        },
        '.ButtonsContainer': {
          marginTop: '50px'
        },
        '.MuiFormControl-root.MuiTextField-root': {
          marginBottom: '14px'
        }
      }
    },
    /*   MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: '14px', // Global font size for input text
          padding: '4px 14px', // Padding inside the input box
        },
      },
    }, */
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          fontSize: '13px',
          '& .MuiSelect-select': {
            padding: '8px 16px' // Adjust padding within the select box
          },
          '& .MuiOutlinedInput-input': {
            padding: '8px 14px' // Adjust padding within the input box
          },
          backgroundColor: vars.primaryBackgroundLight3
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '14px', // Font size for each item in the dropdown
          padding: '8px 16px'
          /*   '&:hover': {
            backgroundColor: 'red', // Hover effect for dropdown items
          }, */
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: vars.primaryLabelColor,
          transform: 'translate(14px, 8px) scale(1)'
        },
        shrink: {
          transform: 'translate(14px, -8px) scale(0.75)' // Adjust position when focused/shrunk
        },
        formControl: {
          fontSize: '13px',
          opacity: vars.primaryOpacity // Adjust font size when used with form control
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        standardInfo: {
          backgroundColor: vars.primaryBackgroundLight, // rgba(127, 0, 255, 0.1)
          '& .MuiAlert-icon': {
            color: vars.primaryOp2 // rgb(1, 67, 97)
          }
        }
      }
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '& .MuiAutocomplete-inputRoot': {
            fontSize: '14px', // Global font size for the input box
            padding: '4px 14px !important' // Padding inside the input box
          }
        },
        option: {
          padding: '10px 14px', // Padding for each item in the dropdown
          fontSize: '14px' // Font size for each item in the dropdown
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontSize: '13px',
          padding: 0,
          '&.custom-chip': {
            background: vars.primaryOp1,
            color: vars.primary,
            border: '1px solid rgba(75, 0, 129, 0.2)',
            fontWeight: 500,
            transition: 'all 0.2s ease',
            '&:hover': {
              background: 'rgba(75, 0, 129, 0.15)'
            },
            '&.highlighted': {
              background: vars.primary,
              color: 'white',
              borderColor: vars.primary
            }
          }
        }
      }
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          '&.UserAvatar': {
            width: '3.75rem',
            height: '3.75rem',
            fontSize: '1.5rem',
            border: '2px solid ' + vars.lightGray,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
          }
        }
      }
    },
    MuiPickersYear: {
      styleOverrides: {
        root: {
          fontSize: '13px',
          padding: '0', // Adjust padding to control button size
          '& .MuiPickersYear-yearButton': {
            padding: 0, // Adjust padding for year buttons
            fontSize: '13px', // Adjust font size for year buttons
            height: 30,
            width: 50,
            margin: '4px 0',
            '& .Mui-selected': {
              backgroundColor: vars.primary // Adjust background color for selected year
            }
          }
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none' as const
        }
      }
    }
  },
  typography: {
    fontFamily: [
      'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    ].join(','),
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 600,
    button: {
      textTransform: 'none' as const
    }
  }
};

declare module '@mui/material/styles' {
  interface TypographyVariants {
    title: React.CSSProperties;
    subtitle: React.CSSProperties;
    text: React.CSSProperties;
    labelText: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    title?: React.CSSProperties;
    subtitle?: React.CSSProperties;
    text?: React.CSSProperties;
    labelText?: React.CSSProperties;
  }
}

let theme = createTheme(recOneTheme);

// Extend theme with custom typography variants
theme = createTheme(theme, {
  typography: {
    title: {
      fontSize: 22,
      fontWeight: 600,
      lineHeight: '28px'
    },
    subtitle: {
      fontSize: 15,
      fontWeight: 500,
      lineHeight: '24px',
      letterSpacing: '0.1px'
    },
    text: {
      fontSize: 14,
      fontWeight: 400,
      lineHeight: '20px',
      letterSpacing: '0.1px'
    },
    labelText: {
      fontSize: 12,
      fontWeight: 400,
      lineHeight: '16px',
      letterSpacing: '0.5px'
    }
  }
});

theme = responsiveFontSizes(theme);

export default theme;
